import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { formatDate as fmtDateUtil, getPerthToday } from '../utils/dateUtils';
import {
  BarChart3, FileText, TrendingUp, Users, Briefcase, Receipt, ClipboardList,
  DollarSign, ThumbsDown, Package, Share2
} from 'lucide-react';
import api from '../services/api';

import { OverviewTab } from '../components/reports/OverviewTab';
import { QuotesTab } from '../components/reports/QuotesTab';
import { JobsTab } from '../components/reports/JobsTab';
import { InvoicesTab } from '../components/reports/InvoicesTab';
import { ClientsTab } from '../components/reports/ClientsTab';
import { KPIsTab } from '../components/reports/KPIsTab';
import { ItemsUsageTab } from '../components/reports/ItemsUsageTab';
import { LostQuotesTab } from '../components/reports/LostQuotesTab';
import { PnLTab } from '../components/reports/PnLTab';
import { ReferralsTab } from '../components/reports/ReferralsTab';


const TABS = [
  { key: 'overview', label: 'Overview', icon: BarChart3 },
  { key: 'quotes', label: 'Quotes', icon: ClipboardList },
  { key: 'jobs', label: 'Jobs', icon: Briefcase },
  { key: 'invoices', label: 'Invoices', icon: Receipt },
  { key: 'clients', label: 'Clients', icon: Users },
  { key: 'referrals', label: 'Referrals', icon: Share2 },
  { key: 'lost_quotes', label: 'Lost Quotes', icon: ThumbsDown },
  { key: 'pnl', label: 'P&L', icon: DollarSign },
  { key: 'items_usage', label: 'Items Usage', icon: Package },
  { key: 'kpis', label: 'KPIs', icon: TrendingUp },
];

const DATE_PRESETS = [
  { label: 'Last Month', value: 'last_month' },
  { label: 'Last Quarter', value: 'last_quarter' },
  { label: 'Year To Date', value: 'ytd' },
  { label: 'Custom', value: 'custom' },
];

function getPresetDates(preset) {
  const perthNow = new Date(new Date().toLocaleString('en-US', { timeZone: 'Australia/Perth' }));
  const pad = d => String(d).padStart(2, '0');
  const toYMD = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
  let start, end;
  if (preset === 'last_month') {
    start = new Date(perthNow.getFullYear(), perthNow.getMonth() - 1, 1);
    end = new Date(perthNow.getFullYear(), perthNow.getMonth(), 0);
  } else if (preset === 'last_quarter') {
    const qm = Math.floor(perthNow.getMonth() / 3) * 3;
    start = new Date(perthNow.getFullYear(), qm - 3, 1);
    end = new Date(perthNow.getFullYear(), qm, 0);
  } else if (preset === 'ytd') {
    start = new Date(perthNow.getFullYear(), 0, 1);
    end = perthNow;
  } else {
    return null;
  }
  return { start: toYMD(start), end: toYMD(end) };
}

const fmtDate = (d) => fmtDateUtil(d);

export default function Reports() {
  const [activeTab, setActiveTab] = useState('overview');
  const [preset, setPreset] = useState('ytd');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [quoteOpenOnly, setQuoteOpenOnly] = useState(false);

  const { start_date, end_date } = useMemo(() => {
    if (preset === 'custom') return { start_date: customStart, end_date: customEnd };
    const d = getPresetDates(preset);
    return d ? { start_date: d.start, end_date: d.end } : { start_date: '', end_date: '' };
  }, [preset, customStart, customEnd]);

  const enabled = !!(start_date && end_date);

  const summary = useQuery({
    queryKey: ['report-summary', start_date, end_date],
    queryFn: () => api.get(`/api/reports/summary?start_date=${start_date}&end_date=${end_date}`).then(r => r.data),
    enabled: enabled && activeTab === 'overview',
  });

  const quotes = useQuery({
    queryKey: ['report-quotes', start_date, end_date, quoteOpenOnly],
    queryFn: () => api.get(`/api/reports/quotes?start_date=${start_date}&end_date=${end_date}&open_only=${quoteOpenOnly}`).then(r => r.data),
    enabled: enabled && activeTab === 'quotes',
  });

  const jobs = useQuery({
    queryKey: ['report-jobs', start_date, end_date],
    queryFn: () => api.get(`/api/reports/jobs?start_date=${start_date}&end_date=${end_date}`).then(r => r.data),
    enabled: enabled && activeTab === 'jobs',
  });

  const invoices = useQuery({
    queryKey: ['report-invoices', start_date, end_date],
    queryFn: () => api.get(`/api/reports/invoices?start_date=${start_date}&end_date=${end_date}`).then(r => r.data),
    enabled: enabled && activeTab === 'invoices',
  });

  const clients = useQuery({
    queryKey: ['report-clients', start_date, end_date],
    queryFn: () => api.get(`/api/reports/clients?start_date=${start_date}&end_date=${end_date}`).then(r => r.data),
    enabled: enabled && activeTab === 'clients',
  });

  const referrals = useQuery({
    queryKey: ['report-referrals', start_date, end_date],
    queryFn: () => api.get(`/api/reports/referrals?start_date=${start_date}&end_date=${end_date}`).then(r => r.data),
    enabled: enabled && activeTab === 'referrals',
  });

  const kpis = useQuery({
    queryKey: ['report-kpis', start_date, end_date],
    queryFn: () => api.get(`/api/reports/kpis?start_date=${start_date}&end_date=${end_date}`).then(r => r.data),
    enabled: enabled && activeTab === 'kpis',
  });

  const pnl = useQuery({
    queryKey: ['report-pnl', start_date, end_date],
    queryFn: () => api.get(`/api/reports/pnl?start_date=${start_date}&end_date=${end_date}`).then(r => r.data),
    enabled: enabled && activeTab === 'pnl',
  });

  const lostQuotes = useQuery({
    queryKey: ['report-lost-quotes', start_date, end_date],
    queryFn: () => api.get(`/api/reports/lost-quotes?start_date=${start_date}&end_date=${end_date}`).then(r => r.data),
    enabled: enabled && activeTab === 'lost_quotes',
  });

  const itemsUsage = useQuery({
    queryKey: ['report-items-usage', start_date, end_date],
    queryFn: () => api.get(`/api/reports/items-usage?start_date=${start_date}&end_date=${end_date}`).then(r => r.data),
    enabled: enabled && activeTab === 'items_usage',
  });

  const exportExcel = async (title, headers, rows, summaryRow) => {
    try {
      const res = await api.post('/api/reports/export-excel',
        { title, headers, rows, summary_row: summaryRow, date_range: `${start_date} to ${end_date}` },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `${title}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success('Excel exported');
    } catch { toast.error('Export failed'); }
  };

  const printReport = () => {
    const content = document.querySelector('[data-testid="reports-page"]');
    // Open a NEW top-level window and print there. This is reliable even when
    // the app is embedded in a sandboxed preview iframe (where a direct
    // window.print() on the inner frame is blocked). Tailwind `print:` variants
    // still apply during the new window's print, so the print-only header shows
    // and the on-screen filter bar / tabs are hidden as intended.
    const printWin = content ? window.open('', '_blank', 'width=1200,height=800') : null;
    if (!content || !printWin) {
      window.print(); // fallback (e.g. popups blocked)
      return;
    }
    const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
      .map(el => el.outerHTML).join('\n');
    printWin.document.open();
    printWin.document.write(
      `<!doctype html><html><head><meta charset="utf-8">` +
      `<base href="${window.location.origin}/">` +
      `<title>${activeTabLabel} Report</title>${styles}` +
      `<style>body{background:#fff;margin:0;padding:16px;}</style>` +
      `</head><body>${content.outerHTML}</body></html>`
    );
    printWin.document.close();
    printWin.focus();
    // Give styles + charts a moment to render, then trigger the print dialog.
    setTimeout(() => { try { printWin.print(); } catch (e) { /* no-op */ } }, 600);
  };

  const activeTabLabel = TABS.find(t => t.key === activeTab)?.label || '';

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="reports-page">
      {enabled && (
        <div className="hidden print:block mb-4 border-b pb-3" data-testid="report-print-header">
          <h1 className="text-xl font-bold text-slate-900">{activeTabLabel} Report</h1>
          <p className="text-sm text-slate-700 mt-0.5">
            Period: <span className="font-semibold">{fmtDate(start_date)}</span> to <span className="font-semibold">{fmtDate(end_date)}</span>
          </p>
          <p className="text-xs text-slate-500 mt-0.5">Generated: {fmtDate(getPerthToday())}</p>
        </div>
      )}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 font-heading">Reports</h1>
          <p className="text-slate-500 text-sm mt-1">Analytics and reporting dashboard</p>
        </div>
        <div className="flex gap-2 print:hidden">
          <button onClick={printReport} className="flex items-center gap-1.5 px-3 py-2 bg-slate-600 text-white rounded text-sm hover:bg-slate-700" data-testid="print-btn">
            <FileText size={15} /> PDF / Print
          </button>
        </div>
      </div>

      <div className="bg-white rounded-[10px] border p-4 print:hidden">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Date Range</label>
            <select
              value={preset}
              onChange={e => setPreset(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded text-sm w-44"
              data-testid="date-preset"
            >
              {DATE_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          {preset === 'custom' && (
            <>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Start Date</label>
                <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
                  className="px-3 py-2 border border-slate-300 rounded text-sm w-44" data-testid="report-start-date" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">End Date</label>
                <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
                  className="px-3 py-2 border border-slate-300 rounded text-sm w-44" data-testid="report-end-date" />
              </div>
            </>
          )}
          {enabled && (
            <div className="text-sm text-slate-500 py-2">
              Showing: <span className="font-medium text-slate-700">{fmtDate(start_date)}</span> to <span className="font-medium text-slate-700">{fmtDate(end_date)}</span>
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-1 overflow-x-auto border-b print:hidden" data-testid="report-tabs">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${activeTab === tab.key ? 'border-[#f4931d] text-[#f4931d]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              data-testid={`tab-${tab.key}`}>
              <Icon size={15} />{tab.label}
            </button>
          );
        })}
      </div>

      {!enabled ? (
        <div className="text-center py-16 text-slate-400">Please select a date range to view reports</div>
      ) : (
        <>
          {activeTab === 'overview' && <OverviewTab data={summary.data} loading={summary.isLoading} />}
          {activeTab === 'quotes' && <QuotesTab data={quotes.data} loading={quotes.isLoading} openOnly={quoteOpenOnly} setOpenOnly={setQuoteOpenOnly} onExport={exportExcel} />}
          {activeTab === 'jobs' && <JobsTab data={jobs.data} loading={jobs.isLoading} onExport={exportExcel} />}
          {activeTab === 'invoices' && <InvoicesTab data={invoices.data} loading={invoices.isLoading} onExport={exportExcel} />}
          {activeTab === 'clients' && <ClientsTab data={clients.data} loading={clients.isLoading} onExport={exportExcel} startDate={start_date} endDate={end_date} />}
          {activeTab === 'referrals' && <ReferralsTab data={referrals.data} loading={referrals.isLoading} onExport={exportExcel} />}
          {activeTab === 'lost_quotes' && <LostQuotesTab data={lostQuotes.data} loading={lostQuotes.isLoading} onExport={exportExcel} />}
          {activeTab === 'pnl' && <PnLTab data={pnl.data} loading={pnl.isLoading} onExport={exportExcel} />}
          {activeTab === 'items_usage' && <ItemsUsageTab data={itemsUsage.data} loading={itemsUsage.isLoading} onExport={exportExcel} />}
          {activeTab === 'kpis' && <KPIsTab data={kpis.data} loading={kpis.isLoading} onExport={exportExcel} />}
        </>
      )}
    </div>
  );
}
